import { useCallback, useEffect, useRef, useState } from "react";

import { ArrowLeft } from "lucide-react";

import { getDiagnosis, startDiagnosis } from "../api/persona";
import { LLM_COLORS } from "../utils/llmColors";
import PersonaFitDetail from "./PersonaFitDetail";
import PersonaPromptExplorer from "./PersonaPromptExplorer";
import PersonaRecommendations from "./PersonaRecommendations";

const useDiagnosis = (reportId, personaId) => {
  const [diagnosis, setDiagnosis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [error, setError] = useState(null);
  const inFlightRef = useRef(false);

  const fetchDiagnosis = useCallback(async () => {
    if (inFlightRef.current || fetched) return;
    inFlightRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const res = await getDiagnosis(reportId, personaId);
      setDiagnosis(res.data);
    } catch (err) {
      if (err?.response?.status !== 404) {
        setError("Failed to fetch diagnosis.");
      }
    } finally {
      inFlightRef.current = false;
      setFetched(true);
      setLoading(false);
    }
  }, [reportId, personaId, fetched]);

  const startNew = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    setError(null);
    setDiagnosis(null);
    try {
      const res = await startDiagnosis(reportId, personaId);
      setDiagnosis(res.data);
    } catch {
      setError("Failed to run diagnosis.");
    } finally {
      inFlightRef.current = false;
      setFetched(true);
      setLoading(false);
    }
  }, [reportId, personaId]);

  return { diagnosis, setDiagnosis, loading, fetched, error, fetchDiagnosis, startNew };
};

const scoreTextColor = (score) => {
  if (score >= 80) return "text-emerald-300";
  if (score >= 60) return "text-emerald-400";
  if (score >= 50) return "text-yellow-400";
  return "text-orange-400";
};

// Diagnosis and Fixes were two halves of one thing — every fix is generated from a
// gap — so they are one tab, with each gap shown as the "why" on its own fix card.
const TABS = ["Overview", "Prompts", "Recommendations"];
const RECOMMENDATIONS_TAB = "Recommendations";

const PersonaDetail = ({ result, reportId, onBack }) => {
  const [activeTab, setActiveTab] = useState("Overview");

  const {
    diagnosis,
    setDiagnosis,
    loading: diagLoading,
    fetched: diagFetched,
    error: diagError,
    fetchDiagnosis,
    startNew,
  } = useDiagnosis(reportId, result.persona_id);

  // Lazy-load: fetch the diagnosis only when its tab is first opened
  useEffect(() => {
    if (activeTab === RECOMMENDATIONS_TAB) {
      fetchDiagnosis();
    }
  }, [activeTab, fetchDiagnosis]);

  const diagProps = {
    diagnosis,
    loading: diagLoading,
    fetched: diagFetched,
    error: diagError,
    onStart: startNew,
    personaLabel: result.persona_label,
    emoji: result.emoji,
    score: result.visibility_score,
  };

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-on-surface-variant hover:text-on-surface transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to All Personas
      </button>

      {/* Header */}
      <div className="bg-surface-container-low rounded-2xl p-6 border border-surface-container-high">
        <div className="flex items-center gap-4 mb-4">
          <span className="text-4xl">{result.emoji}</span>
          <div>
            <h2 className="font-headline text-2xl font-bold">{result.persona_label}</h2>
            <p className="text-on-surface-variant text-sm">Persona visibility breakdown</p>
            {result.scoring_version && (
              <p className="text-xs text-on-surface-variant/50 mt-0.5">{result.scoring_version}</p>
            )}
          </div>
          <div className="ml-auto text-right">
            <div
              className={`text-4xl font-extrabold font-headline ${scoreTextColor(result.visibility_score)}`}
            >
              {result.visibility_score}
            </div>
            <div className="text-xs text-on-surface-variant">/ 100</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="bg-surface-container-lowest rounded-xl p-3 border border-surface-container-high">
            <div className="text-xl font-bold">{Math.round(result.mention_rate * 100)}%</div>
            <div className="text-xs text-on-surface-variant mt-1">Mention Rate</div>
          </div>
          <div className="bg-surface-container-lowest rounded-xl p-3 border border-surface-container-high">
            <div className="text-xl font-bold">
              {result.mentioned_runs}/{result.total_runs}
            </div>
            <div className="text-xs text-on-surface-variant mt-1">Prompt Runs</div>
          </div>
          <div className="bg-surface-container-lowest rounded-xl p-3 border border-surface-container-high">
            <div className="text-xl font-bold">
              {result.avg_rank != null ? `#${result.avg_rank.toFixed(1)}` : "—"}
            </div>
            <div className="text-xs text-on-surface-variant mt-1">Avg. Rank</div>
            {result.avg_rank == null && (
              <div className="text-[10px] leading-tight text-on-surface-variant/70 mt-1">
                {result.mentioned_runs > 0
                  ? "Mentioned, but never shown in a ranked list"
                  : "Not mentioned in any AI responses yet"}
              </div>
            )}
          </div>
        </div>

        {/* Low score diagnose CTA */}
        {result.visibility_score < 50 && activeTab !== RECOMMENDATIONS_TAB && (
          <button
            onClick={() => setActiveTab(RECOMMENDATIONS_TAB)}
            className="mt-4 w-full py-2 rounded-xl text-sm font-bold bg-amber-500/20 text-amber-800 border border-amber-500/30 hover:bg-amber-500/30 transition-colors"
          >
            Score &lt; 50 — Diagnose &amp; Fix →
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-surface-container-high pb-0">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${
              activeTab === tab
                ? "text-primary border-b-2 border-primary -mb-px"
                : "text-on-surface-variant hover:text-on-surface"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "Overview" && (
        <div className="space-y-4">
          <PersonaFitDetail result={result} />

          <h3 className="font-headline text-lg font-bold">Prompt Coverage</h3>
          <div className="space-y-3">
            {(result.prompt_coverage ?? [])
              .filter((pc) => pc.prompt_type !== "validation")
              .map((pc) => (
                <div
                  key={pc.prompt_id}
                  className="bg-surface-container-low rounded-xl p-4 border border-surface-container-high"
                >
                  <p className="text-sm font-medium mb-3">{pc.prompt_text}</p>
                  <div className="flex flex-wrap gap-2">
                    {pc.engine_results.map((er) => {
                      const colors = LLM_COLORS[er.engine] || {
                        bg: "bg-surface-container-high",
                        text: "text-on-surface-variant",
                        border: "border-outline-variant/30",
                        label: er.engine,
                      };
                      return (
                        <div
                          key={er.engine}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold ${colors.bg} ${colors.text} ${colors.border}`}
                        >
                          <span>{colors.label}</span>
                          {er.mentioned ? (
                            <span className="opacity-80">#{er.rank}</span>
                          ) : (
                            <span className="opacity-50">not mentioned</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {activeTab === "Prompts" && <PersonaPromptExplorer promptCoverage={result.prompt_coverage} />}

      {activeTab === RECOMMENDATIONS_TAB && reportId && (
        <PersonaRecommendations
          {...diagProps}
          onUpdated={setDiagnosis}
          reportId={reportId}
          personaId={result.persona_id}
        />
      )}
    </div>
  );
};

export default PersonaDetail;
